// Personal access tokens (#835) — `GET/POST/PATCH/DELETE /v1/tokens`. The credential
// a human pastes into an MCP client, a CI job or a script to talk to OUR API.
//
// Not to be confused with either neighbour on the settings rail: `lib/keys.ts`
// holds BYOK *provider* credentials (Anthropic/OpenAI keys we spend on the
// account's behalf) and `lib/connections.ts` holds vault secrets
// (`dashboard:<handle>`, referenced from `.maintainer.yml`). Those are secrets
// the platform CONSUMES; this is the one it MINTS.

import type { ApiTokenWire } from '@developerz/domain';
import { ApiTokenWireSchema, Scope } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { type ApiCall, sendApiCall } from './api-call';
import type { Page } from './paged';
import { pageSchema } from './paged-schema';
import { SESSION_TOKEN } from './session';

/**
 * One masked token row — `ApiTokenWireSchema` from `@developerz/domain`, THE
 * PUBLISHED RESOURCE, not a copy of it. The same schema `apps/api` serializes
 * `GET /v1/tokens` through (`ApiTokenResource`), so there is no second
 * declaration left to drift.
 *
 * The prose the mirror carried is on the resource's own fields, where every
 * reader of the shape gets it rather than only this app: `name` is the ONLY
 * field that tells two credentials apart (`prefix` is the same literal on every
 * token the platform has ever minted, and the secret exists for exactly one
 * render); `expires_iso` null is a policy answer per mint reason, not an
 * omission; `runner_id` is set on a box connection token (#824) and null on a
 * human-minted PAT.
 */
export type ApiToken = Readonly<ApiTokenWire>;

/**
 * `GET /v1/tokens` — the roster PLUS the vocabulary a new token may carry.
 *
 * The rows ride the canonical `{data, next_cursor}` envelope with `next_cursor`
 * always null: an account's credentials are a roster a human maintains, served
 * whole, so there is no second page to reach and no Load-more to offer.
 */
export type ApiTokenList = Page<ApiToken> & {
  readonly mintable_scopes: readonly string[];
};

/**
 * The roster body, PARSED (plan 101 slice 15 step 3). `mintable_scopes` stays
 * `z.string()` deliberately — it is the SERVER's answer about what this session
 * may mint, computed from the caller's own grant, and a client-side scope enum
 * would be this app second-guessing an authorization decision it does not own.
 */
const apiTokenListSchema = pageSchema(ApiTokenWireSchema).extend({
  mintable_scopes: z.array(z.string()),
});

/** What a mint answers: the plaintext token, exactly once. */
export type MintedToken = {
  readonly id: string;
  readonly token: string;
  readonly scopes: readonly string[];
  /** The label the mint stored — echoed from the row, not from what was sent. */
  readonly name: string | null;
};

/** What a mint carries: the label, and the scopes (`null` = the caller's whole set). */
export type MintInput = {
  readonly name: string;
  readonly scopes: readonly string[] | null;
};

export const API_TOKENS_QUERY_KEY = 'api_tokens';

/** The account's tokens. Idle with no session, like the other feature reads. */
export function useApiTokens(token: string | null = SESSION_TOKEN) {
  return createQuery<ApiTokenList>(() => ({
    queryKey: [API_TOKENS_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch('/v1/tokens', token, undefined, { schema: apiTokenListSchema }),
  }));
}

/**
 * Mint a PAT. `scopes` narrows the token; `null` takes everything this session
 * can mint (the route's own default). The plaintext `token` comes back ONCE and
 * is never stored — it lives in the caller's signal until the modal closes.
 */
export async function mintApiToken(
  input: MintInput,
  token: string | null = SESSION_TOKEN,
): Promise<MintedToken> {
  return sendApiCall<MintedToken>(tokenMintCall(input), token);
}

/**
 * The mint as one value — fired by `mintApiToken`, shown by `<CopyAsCall/>`.
 * Omitting `scopes` is not the same as sending an empty list: the route reads
 * absence as "everything the caller may mint", which is what `null` means here.
 */
export function tokenMintCall({ name, scopes }: MintInput): ApiCall {
  const body = { name, ...(scopes === null ? {} : { scopes: [...scopes] }) };
  return {
    capability: 'token.mint',
    method: 'POST',
    path: '/v1/tokens',
    body,
    mcp: { kind: 'verb', verb: 'token.mint', arguments: body },
  };
}

/**
 * Relabel a token. Moves the human name and nothing else — no scope, no expiry,
 * no revocation state — which is why a REVOKED row is renameable too: labelling
 * a credential you just killed is how the roster stays readable.
 */
export async function renameApiToken(
  id: string,
  name: string,
  token: string | null = SESSION_TOKEN,
): Promise<{ id: string; name: string }> {
  return sendApiCall(tokenRenameCall(id, name), token);
}

/** The rename as one value. REST puts the id in the path; the verb names it `token_id`. */
export function tokenRenameCall(id: string, name: string): ApiCall {
  return {
    capability: 'token.rename',
    method: 'PATCH',
    path: `/v1/tokens/${encodeURIComponent(id)}`,
    body: { name },
    mcp: { kind: 'verb', verb: 'token.rename', arguments: { token_id: id, name } },
  };
}

/** Revoke a token. Idempotent server-side; the row stays, stamped `revoked_iso`. */
export async function revokeApiToken(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<{ id: string; revoked_iso: string }> {
  return sendApiCall(tokenRevokeCall(id), token);
}

/** The revoke as one value. The MCP verb names the id `token_id`, REST puts it in the path. */
export function tokenRevokeCall(id: string): ApiCall {
  return {
    capability: 'token.revoke',
    method: 'DELETE',
    path: `/v1/tokens/${encodeURIComponent(id)}`,
    mcp: { kind: 'verb', verb: 'token.revoke', arguments: { token_id: id } },
  };
}

/**
 * Whether to offer this caller the MINT control. Server-derived, NOT a
 * client-side role mirror: `mintable_scopes` is the domain's `mintableScopeSet`
 * over the caller's own grant (`packages/domain/src/scopes.ts`), empty exactly
 * when the caller may not mint at all — so the vocabulary IS the permission,
 * and it can't drift from the route the way `lib/roles.ts` could if the
 * ceiling or the mint gate moved. Undefined (mid-load) ⇒ shut, so the control
 * never goes live before the answer lands.
 *
 * It answers the mint ONLY. Rename and revoke are a different reach (the
 * domain's `tokenReach`, `manage`): a caller who may mint may still manage only
 * their own rows, so a row's controls never ride this gate.
 */
export const canMintTokens = (list: ApiTokenList | undefined): boolean =>
  (list?.mintable_scopes.length ?? 0) > 0;

/**
 * Whether to offer this caller the RENAME + REVOKE controls — the account's
 * own credential roster.
 *
 * Its own answer, and NOT the mint vocabulary's: the mint opens on
 * `write:tasks` OR `write:account`, while rename and revoke gate on
 * `write:tasks` with `write:account` admitted beside it, and the server fences
 * each row (`tokenReach`). This answer says only that the caller holds the
 * account's ADMINISTRATION scope — the reach that manages every row — so a
 * caller without it must not be offered controls the server would refuse.
 *
 * The answer is the caller's OWN published scope list (`/v1/me`'s `scopes`),
 * the server's authority on what this credential may do — never a client-side
 * role mirror (`lib/roles.ts`), which drifts the day the ceiling moves.
 *
 * Undefined (mid-load, or a `/v1/me` that published no list) ⇒ shut: a control
 * that goes live before the answer lands is a 403 waiting to happen.
 */
export const canRevokeTokens = (scopes: readonly string[] | undefined): boolean =>
  (scopes ?? []).includes(Scope.WriteAccount);

/** The read-only preset: every `read:*` scope this caller could mint. */
export const readOnlyScopes = (mintable: readonly string[]): string[] =>
  mintable.filter((s) => s.startsWith('read:'));

/**
 * The RESOURCE a scope acts on — `read:repos` → `repos`, `write:self:keys` →
 * `self`. The verb is dropped because it is what the picker's two columns
 * already say; what a reader scans for is the noun.
 *
 * A scope with no `read:`/`write:` verb (`use:integration:slack`, `admin:goals`)
 * groups under its FIRST segment, so nothing falls out of the picker. Neither
 * class can reach the mint vocabulary today — `mintableScopes` strips both — but
 * a grouping that silently dropped a scope the server offered would be a
 * checkbox a caller cannot tick.
 */
export const scopeGroup = (scope: string): string => {
  const [verb, second] = scope.split(':');
  return (verb === 'read' || verb === 'write' ? second : verb) ?? scope;
};

/**
 * The mintable vocabulary as `{group, scopes}` blocks, in the SERVER's order —
 * a group takes the position of its first scope, and the scopes inside keep
 * theirs. That order is the role ceiling's, which already pairs each resource's
 * read and write, so the grouping renders the structure the flat list had and
 * could not show: 23 identical-looking checkboxes in a scroll box.
 *
 * Nothing is dropped and nothing is added: flattening the result reproduces the
 * input exactly, which is what `tokens.test.ts` pins.
 */
export function groupScopes(
  mintable: readonly string[],
): { readonly group: string; readonly scopes: readonly string[] }[] {
  const byGroup = new Map<string, string[]>();
  for (const scope of mintable) {
    const group = scopeGroup(scope);
    const bucket = byGroup.get(group);
    if (bucket) bucket.push(scope);
    else byGroup.set(group, [scope]);
  }
  return [...byGroup].map(([group, scopes]) => ({ group, scopes }));
}

/** A box connection token (#824) vs a human-minted PAT. */
export type TokenKind = 'pat' | 'box';
export const tokenKind = (t: ApiToken): TokenKind => (t.runner_id === null ? 'pat' : 'box');

export const isRevoked = (t: ApiToken): boolean => t.revoked_iso !== null;

/**
 * Past its own expiry — a token the API now refuses with a 401 the holder can
 * only clear by minting a new one. Read from the row rather than remembered
 * from the mint notice, which is the ONE time the lifetime used to be said.
 *
 * `expires_iso` is the FIRST INVALID instant, not the last valid one, so the
 * comparison is `<=` — the same boundary `resolveApiToken`'s rung 0 refuses on
 * (`row.expiresAt <= now`, `packages/db/src/queries/tokens.ts`), pinned there
 * and here. `<` would paint a token "Active" in the exact millisecond the API
 * starts answering 401 for it, which is the disagreement this mirrors away.
 *
 * `now` is a parameter so the check is testable without moving the machine
 * clock; a malformed date reads as NOT expired, because claiming a live
 * credential is dead is the worse of the two wrong answers.
 */
export const isExpired = (t: ApiToken, now: number = Date.now()): boolean => {
  if (t.expires_iso === null) return false;
  const at = new Date(t.expires_iso).getTime();
  return !Number.isNaN(at) && at <= now;
};

/**
 * Revocable from THIS page: a live, human-minted PAT.
 *
 * A box's connection token is listed (an account should see every credential
 * that can reach its API) but not revoked here — severing it would strand a box
 * the fleet still believes is enrolled. `/fleet` owns that lever (`revokeRunner`
 * revokes the runner AND its tokens together), so this page links there instead
 * of half-owning the box lifecycle — same rule as webhooks: create where it lives.
 */
export const isRevocableHere = (t: ApiToken): boolean => tokenKind(t) === 'pat' && !isRevoked(t);
